import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, UserPlus, Users } from 'lucide-react';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Heading } from '@/Components/ui/typography';
import {
  listSavedInterviewers,
  upsertSavedInterviewer,
  deleteSavedInterviewer,
} from '../Applications/savedInterviewersApi';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';
import { invitePerson } from './teamApi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialsFor = (entry) => {
  const source = (entry?.interviewer_name || entry?.interviewer_email || '?').trim();
  if (!source) return '?';
  const parts = source.split(/[.\s@_-]+/).filter(Boolean);
  const seed = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
  return seed.toUpperCase();
};

/**
 * Manage the per-company directory of interviewer emails surfaced as a dropdown
 * in MeetingCreateDialog. HR can add as many as they want; the list is shared
 * across everyone on that company account. Free-text emails in the dialog still
 * work — saving here is purely a convenience.
 */
const SavedInterviewersSection = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  // Track in-flight deletes so the row spinner doesn't depend on a global state.
  const [deletingEmail, setDeletingEmail] = useState('');
  const [invitingEmail, setInvitingEmail] = useState('');

  // A saved interviewer can get their own login, to see all their interviews in one place.
  const { can, teamEnabled } = useCompanyAccess();
  const canInvite = teamEnabled && can('team.manage');

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadItems = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const rows = await listSavedInterviewers();
      if (mountedRef.current) setItems(rows);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not load saved interviewers.';
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current && !silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleAdd = useCallback(async () => {
    setError('');
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Interviewer email is required.');
      return;
    }
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    try {
      await upsertSavedInterviewer({
        interviewerEmail: normalizedEmail,
        interviewerName: name,
        jobTitle,
      });
      toast.success('Interviewer saved');
      setEmail('');
      setName('');
      setJobTitle('');
      await loadItems({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not save interviewer.';
      setError(msg);
      toast.error(msg);
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  }, [email, name, jobTitle, loadItems]);

  const handleInvite = useCallback(async (target) => {
    setInvitingEmail(target.interviewer_email);
    try {
      await invitePerson({
        email: target.interviewer_email,
        fullName: target.interviewer_name || target.interviewer_email.split('@')[0],
        roleKey: 'interviewer',
      });
      toast.success(`Invite sent to ${target.interviewer_email}. They set a password from the email.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the invite.');
    } finally {
      if (mountedRef.current) setInvitingEmail('');
    }
  }, []);

  const handleRemove = useCallback(async (target) => {
    if (!target?.interviewer_email) return;
    if (typeof window !== 'undefined' && !window.confirm(
      `Remove ${target.interviewer_email} from saved interviewers?`
    )) {
      return;
    }
    setDeletingEmail(target.interviewer_email);
    try {
      await deleteSavedInterviewer({ interviewerEmail: target.interviewer_email });
      toast.success('Interviewer removed');
      await loadItems({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Could not remove interviewer.';
      toast.error(msg);
    } finally {
      if (mountedRef.current) setDeletingEmail('');
    }
  }, [loadItems]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aName = (a?.interviewer_name || a?.interviewer_email || '').toLowerCase();
      const bName = (b?.interviewer_name || b?.interviewer_email || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [items]);

  return (
    <Card className="bg-white rounded-2xl shadow-md p-8 w-full">
      <CardContent className="p-0 space-y-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-50 text-sky-600">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <Heading level="h2" className="text-xl font-semibold text-foreground">
              Saved Interviewers
            </Heading>
            <p className="text-sm text-muted-foreground mt-1">
              Add the people on your team who regularly host candidate interviews. They will
              appear as a dropdown when you schedule a meeting, so you don't have to retype
              the email each time. You can still type any email manually when scheduling.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <Label htmlFor="saved-interviewer-email">Email *</Label>
              <Input
                id="saved-interviewer-email"
                type="email"
                placeholder="alex@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="sm:col-span-1">
              <Label htmlFor="saved-interviewer-name">Name (optional)</Label>
              <Input
                id="saved-interviewer-name"
                placeholder="Alex Johnson"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="sm:col-span-1">
              <Label htmlFor="saved-interviewer-role">Role (optional)</Label>
              <Input
                id="saved-interviewer-role"
                placeholder="Engineering Manager"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          {error ? (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              onClick={handleAdd}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add interviewer
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">
              Directory <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{sortedItems.length}</span>
            </div>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            ) : null}
          </div>
          {sortedItems.length === 0 && !isLoading ? (
            <div className="px-4 py-10 text-center">
              <Users className="mx-auto mb-2 h-6 w-6 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">No saved interviewers yet</p>
              <p className="mt-1 text-xs text-slate-500">Add a teammate above so they show up in the meeting create dropdown.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {sortedItems.map((entry) => (
                <li
                  key={entry.interviewer_email}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                    {initialsFor(entry)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {entry.interviewer_name || entry.interviewer_email}
                    </div>
                    <div className="truncate text-xs text-slate-500">
                      {entry.interviewer_email}
                      {entry.job_title ? <span> · {entry.job_title}</span> : null}
                    </div>
                  </div>
                  {canInvite && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleInvite(entry)}
                      disabled={invitingEmail === entry.interviewer_email}
                    >
                      {invitingEmail === entry.interviewer_email ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Invite to log in
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(entry)}
                    disabled={deletingEmail === entry.interviewer_email}
                    className="text-slate-500 hover:text-red-600"
                  >
                    {deletingEmail === entry.interviewer_email ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SavedInterviewersSection;
